/* eslint-disable no-underscore-dangle */
import lod_ from "lodash";
import Joi from "joi";
import MDButton from "components/Basics/MDButton";
import { Divider, Icon, Menu, MenuItem } from "@mui/material";
import MDTypography from "components/Basics/MDTypography";
import ConfirmDialogButton from "components/Custom/Dialogs/ConfirmDialogButton";
import { useFormActions } from "hooks/useFormActions";
import { useLanguages } from "hooks/useLanguages";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { display, hide } from "redux-react/reducers/snackBarReducer";
import { v4 } from "uuid";
import MenuLanguages from "../Assets/Theme/components/MenuLanguages";

const MenuCategory = ({
	anchorEl,
	onClose,
	menuRef,
	folder,
	onAddFile,
	handleEditAsset,
	onDeleteFolder,
	t,
	themeActions
}) => {
	const dispatch = useDispatch();
	const { languages } = useLanguages();
	const [languageMenuAnchorEl, setLanguageMenuAnchorEl] = useState(null);
	const { startCategoryTraining, startCategoryUntraining, translateV2Dataset } = themeActions;
	const { updateItem, getItemsFromCollection } = useFormActions();

	/**
	 * Get answer items by type and parent codes
	 * @param {string} type
	 * @param {array} parentCodes
	 * @returns {Promise<Array>}
	 */
	const getAnswerItems = async (type, parentCodes) => {
		return getItemsFromCollection("answerItem", {
			query: {
				documentType: "answerType",
				type,
				parent: {
					$in: parentCodes
				}
			},
			projection: {
				_id: 1,
				code: 1,
				content: 1
			}
		});
	};

	/**
	 * Translate all categories names from a theme
	 */
	const translateCategoryName = async sourceLanguageCode => {
		const TYPE = "themeCategory";

		let categories = await getItemsFromCollection("answerItem", {
			query: {
				documentType: "answerType",
				type: TYPE,
				_id: folder.code
			},
			projection: {
				_id: 1,
				code: 1,
				content: 1
			}
		});

		categories = (categories || [])
			.map(category => ({
				code: category._id,
				content: category?.content[0] || null
			}))
			.filter(category => !lod_.isNil(category.content));

		const callback = async ({ code, result, success }) => {
			if (success === true) {
				// Update
				const data = {
					values: {
						content: [result]
					},
					target: "answerItem"
				};
				await updateItem(
					code,
					data,
					() => {},
					() => {
						dispatch(
							display({
								message: t("ASSETS.THEME.categoryUpdateError", {
									defaultValue: "Impossible de mettre à jour la catégorie"
								}),
								type: "error"
							})
						);
					}
				);
			} else {
				dispatch(
					display({
						message: t("ASSETS.THEME.translationError", {
							defaultValue:
								"Impossible de traduire la catégorie, vérifiez que la langue source ne soit pas vide"
						}),
						type: "error",
						validation: (
							<MDButton
								color="error"
								variant="outlined"
								sx={{ ml: 1 }}
								onClick={() => {
									dispatch(hide());
								}}
							>
								{t("SETTINGS.close")}
							</MDButton>
						)
					})
				);
			}
		};

		const dataset = lod_
			.chain(categories)
			.map(element => {
				const { code, content } = element;

				const entryObject = {
					[sourceLanguageCode]:
						lod_.get(content, sourceLanguageCode?.toLowerCase()) ||
						lod_.get(content, sourceLanguageCode?.toUpperCase())
				};

				return {
					code,
					entryObject,
					sourceLanguage: sourceLanguageCode,
					callback
				};
			})
			.value();

		const inputSchemaValue = Joi.object().pattern(
			Joi.string().trim().required().description("Code langue").empty(),
			Joi.object({
				text: Joi.string().trim().required().description("Texte").min(1).empty()
			})
		);
		const inputSchemaValueString = `Joi.object().pattern(
					Joi.string().trim().required().description("Code langue").empty(),
					Joi.object({
						text: Joi.string().trim().required().description("Texte").min(1).empty()
					})
				)
				`;

		translateV2Dataset(inputSchemaValue, inputSchemaValueString, dataset, () => {});
		onClose();
	};

	/**
	 * Translate all questions from a theme
	 */
	const translateQuestions = async sourceLanguageCode => {
		const TYPE = "themeQuestion";
		let questions = await getAnswerItems(TYPE, [folder.code]);

		questions = (questions || [])
			.map(question => ({
				code: question._id,
				content: question?.content[0] || null
			}))
			.filter(question => !lod_.isNil(question.content));

		const callback = async ({ code, result, success }) => {
			if (success === true) {
				// Update
				const data = {
					values: {
						content: [result]
					},
					target: "answerItem"
				};
				await updateItem(
					code,
					data,
					() => {},
					() => {
						dispatch(
							display({
								message: t("ASSETS.THEME.questionUpdateError", {
									defaultValue: "Impossible de mettre à jour la question"
								}),
								type: "error"
							})
						);
					}
				);
			} else {
				dispatch(
					display({
						message: t("ASSETS.THEME.translationQuestionError", {
							defaultValue:
								"Impossible de traduire la question, vérifiez que la langue source ne soit pas vide"
						}),
						type: "error",
						validation: (
							<MDButton
								color="error"
								variant="outlined"
								sx={{ ml: 1 }}
								onClick={() => {
									dispatch(hide());
								}}
							>
								{t("SETTINGS.close")}
							</MDButton>
						)
					})
				);
			}
		};

		const dataset = lod_
			.chain(questions)
			.map(element => {
				const { code, content } = element;

				const entryObject = {
					[sourceLanguageCode]:
						lod_.get(content, sourceLanguageCode?.toLowerCase()) ||
						lod_.get(content, sourceLanguageCode?.toUpperCase())
				};

				return {
					code,
					entryObject,
					sourceLanguage: sourceLanguageCode,
					callback
				};
			})
			.value();

		const inputSchemaValue = Joi.object().pattern(
			Joi.string().trim().required().description("Code langue").empty(),
			Joi.object({
				question: Joi.string().trim().required().description("Question").min(1).empty(),
				answer: Joi.string().trim().required().description("Réponse").min(1).empty()
			})
		);
		const inputSchemaValueString = `Joi.object().pattern(
					Joi.string().trim().required().description("Code langue").empty(),
					Joi.object({
						question: Joi.string().trim().required().description("Question").min(1).empty(),
						answer: Joi.string().trim().required().description("Réponse").min(1).empty()
					})
				)`;

		translateV2Dataset(inputSchemaValue, inputSchemaValueString, dataset, () => {});
		onClose();
	};

	return (
		<Menu open={Boolean(anchorEl)} onClose={onClose} anchorEl={anchorEl} action={menuRef}>
			{/* Menu language */}
			<MenuLanguages
				anchor={languageMenuAnchorEl?.currentTarget}
				onClose={() => {
					setLanguageMenuAnchorEl({});
				}}
				languages={languages}
				onClick={code => {
					setLanguageMenuAnchorEl({});
					languageMenuAnchorEl?.cb(code);
				}}
			/>
			{/* Category */}
			<MenuItem disabled style={{ opacity: 1 }}>
				<MDTypography variant="caption">Categorie</MDTypography>
			</MenuItem>
			<MenuItem
				onClick={e => {
					setLanguageMenuAnchorEl({
						currentTarget: e.currentTarget,
						cb: code => {
							translateCategoryName(code);
						}
					});
				}}
			>
				<Icon sx={{ mr: 1 }} fontSize="medium">
					translate
				</Icon>
				Traduire le nom de la catégorie
			</MenuItem>
			<MenuItem onClick={() => handleEditAsset(folder)}>
				<Icon sx={{ mr: 1 }} fontSize="medium">
					edit
				</Icon>
				{t("SETTINGS.edit")}
			</MenuItem>

			{/* Delete category */}
			<ConfirmDialogButton
				onCancel={onClose}
				onConfirm={(e, values) => {
					onClose();
					onDeleteFolder(folder);
				}}
				component={
					<MenuItem
						style={{
							color: "red"
						}}
					>
						<Icon sx={{ mr: 1 }} fontSize="medium">
							delete
						</Icon>
						{t("SETTINGS.delete")}
					</MenuItem>
				}
				title={t("ASSETS.DELETE_CONFIRMATION.title")}
				content={t("ASSETS.THEME.confirmDeleteCategory", {
					name: folder.name
				})}
			/>

			<Divider />

			{/* Content */}
			<MenuItem disabled style={{ opacity: 1 }}>
				<MDTypography variant="caption">Questions / Réponses</MDTypography>
			</MenuItem>

			<MenuItem onClick={() => onAddFile(folder)}>
				<Icon sx={{ mr: 1 }} fontSize="medium">
					add
				</Icon>
				Nouvelle Q/R
			</MenuItem>

			{/* Translate all content from theme */}
			<MenuItem
				onClick={e => {
					setLanguageMenuAnchorEl({
						currentTarget: e.currentTarget,
						cb: code => {
							translateQuestions(code);
						}
					});
				}}
			>
				<Icon sx={{ mr: 1 }} fontSize="medium">
					translate
				</Icon>
				Traduire toutes les Q/R
			</MenuItem>

			{/* Train category dialog */}
			<ConfirmDialogButton
				onCancel={onClose}
				onConfirm={(e, values) => {
					onClose();

					const id = v4();

					// Display a message to the user
					dispatch(
						display({
							id,
							message: t("ASSETS.TRAIN.starting"),
							type: "loading"
						})
					);

					startCategoryTraining([folder.code], "category", id);
				}}
				component={
					<MenuItem>
						<Icon fontSize="medium">model_training</Icon>&nbsp;Entrainer toutes les Q/R
					</MenuItem>
				}
				title={t("ASSETS.THEME.startTrainingCategoryTitle")}
				content={t("ASSETS.THEME.startTrainingCategoryDescription")}
			/>

			{/* Untrain category dialog */}
			<ConfirmDialogButton
				onCancel={onClose}
				onConfirm={(e, values) => {
					onClose();
					dispatch(
						display({
							message: t("ASSETS.THEME.questionsUntrainingStarted"),
							type: "info"
						})
					);

					startCategoryUntraining(
						[folder.code],
						"category",
						() => {
							dispatch(
								display({
									message: t("ASSETS.THEME.questionsUntrainingFinish"),
									type: "success"
								})
							);
						},
						() => {
							dispatch(
								display({
									message: t("ASSETS.THEME.untrainingError"),
									type: "error"
								})
							);
						}
					);
				}}
				component={
					<MenuItem style={{ color: "red" }}>
						<Icon fontSize="medium">model_training</Icon>&nbsp;Désentrainer toutes les Q/R
					</MenuItem>
				}
				title={t("ASSETS.THEME.startUntrainingCategoryTitle")}
				content={t("ASSETS.THEME.startUntrainingCategoryDescription")}
			/>
		</Menu>
	);
};

export default MenuCategory;
